import { useEffect, useState, useRef, useCallback, type ChangeEvent } from "react";
import { useParams } from "react-router-dom";
import { getTrackingData, submitTrackingRating, submitTrackingCompletion, uploadCarteGrise, getPublicInterventions, type TrackingData, type TrackingCompletionPayload, type PublicIntervention } from "@services/api/tracking";
import CarteGriseUploadOverlay from "@components/CarteGriseUploadOverlay";
import type { TrackingStep } from "types/ordre";
// ─── Step config ──────────────────────────────────────────

interface StepConfig {
  key: TrackingStep;
  title: string;
  subtitle: string;
  cta?: { label: string; action: string };
}

const ALL_STEPS: StepConfig[] = [
  { key: "carte_grise_manquante", title: "Carte grise manquante", subtitle: "En attente du dépôt de la carte grise", cta: { label: "Déposer ma carte grise", action: "deposit_carte_grise" } },
  { key: "en_attente_devis", title: "En attente du devis", subtitle: "Nous préparons votre devis" },
  { key: "devis_pret", title: "Devis prêt", subtitle: "Le devis est disponible pour validation", cta: { label: "Voir le devis", action: "view_devis" } },
  { key: "en_attente_depot", title: "En attente du véhicule", subtitle: "Nous attendons le dépôt de votre véhicule" },
  { key: "vehicule_depose", title: "Véhicule déposé", subtitle: "Votre véhicule est en cours d'analyse" },
  { key: "devis_supplementaire", title: "Devis supplémentaire", subtitle: "Un devis supplémentaire est en attente de signature", cta: { label: "Voir le devis", action: "view_devis" } },
  { key: "acompte_manquant", title: "Acompte en attente", subtitle: "En attente du règlement de l'acompte", cta: { label: "Régler mon acompte", action: "pay_acompte" } },
  { key: "reparations_en_cours", title: "Réparations en cours", subtitle: "Les travaux sur votre véhicule sont en cours" },
  { key: "travaux_termines", title: "Travaux terminés", subtitle: "Votre véhicule est bientôt prêt" },
  { key: "facture_disponible", title: "Facture disponible", subtitle: "Votre facture est disponible", cta: { label: "Voir la facture", action: "view_facture" } },
  { key: "solde_en_attente", title: "Paiement final en attente", subtitle: "En attente du règlement du solde", cta: { label: "Régler le solde", action: "pay_solde" } },
  { key: "paiement_valide", title: "Paiement validé", subtitle: "Paiement confirmé" },
  { key: "vehicule_disponible", title: "Véhicule disponible", subtitle: "Votre véhicule est prêt à être récupéré" },
  { key: "vehicule_restitue", title: "Véhicule restitué", subtitle: "Merci de votre confiance !" },
];

const MOBILITY_OPTIONS = [
  { key: "attente", label: "Je peux attendre sur place (1h30)" },
  { key: "velo", label: "Demander un vélo électrique (gratuit)" },
  { key: "pret", label: "Demander un véhicule de prêt (zone limitée)" },
  { key: "location", label: "Demander un véhicule de location (liberté/payant)" },
];

const TIRE_OPTIONS = [
  { key: "premier_prix", label: "Pneus premier prix" },
  { key: "intermediaires", label: "Pneus intermédiaires" },
  { key: "premium", label: "Pneus premium" },
];

// ─── Main component ───────────────────────────────────────

type ScreenState = "loading" | "error" | "ready";

export default function TrackingScreenV2() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<ScreenState>("loading");
  const [data, setData] = useState<TrackingData | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [hoveredStar, setHoveredStar] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!id) { setErrorMsg("Lien invalide."); setState("error"); return; }
    getTrackingData(id)
      .then((d) => { setData(d); setSelectedRating(d.client_rating || 0); setState("ready"); })
      .catch(() => { setErrorMsg("Impossible de charger le suivi."); setState("error"); });
  }, [id]);

  useEffect(() => {
    if (!id || !data) return;
    if (data.tracking_step === "vehicule_restitue") return;
    pollRef.current = setInterval(() => {
      getTrackingData(id).then((d) => {
        setData((prev) => {
          if (!prev) return d;
          if (
            d.tracking_step !== prev.tracking_step ||
            d.devis_requested !== prev.devis_requested ||
            d.devis_signed !== prev.devis_signed ||
            d.devis_supplementaire_requested !== prev.devis_supplementaire_requested ||
            JSON.stringify(d.additional_devis_groups) !== JSON.stringify(prev.additional_devis_groups) ||
            d.acompte_paid !== prev.acompte_paid ||
            d.solde_paid !== prev.solde_paid ||
            d.facture_available !== prev.facture_available ||
            d.facture_pdf_url !== prev.facture_pdf_url ||
            d.payment_link_url !== prev.payment_link_url ||
            d.devis_pdf_url !== prev.devis_pdf_url
          ) return d;
          return prev;
        });
      }).catch(() => {});
    }, 10000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [id, data?.tracking_step]);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-[#F8F9FF] flex items-center justify-center">
        <div className="w-8 h-8 border-[3px] border-[#5C6BFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state === "error" || !data) {
    return (
      <div className="min-h-screen bg-[#F8F9FF] flex flex-col items-center justify-center gap-4 p-5">
        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-sm">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="#9CA3AF">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
        </div>
        <p className="text-[13px] font-medium text-[#6B7280] text-center">{errorMsg}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FF] flex flex-col items-center">
      <div className="w-full max-w-[420px] pb-8">
        <TrackingContentV2
          data={data}
          ordreId={id!}
          hoveredStar={hoveredStar}
          setHoveredStar={setHoveredStar}
          selectedRating={selectedRating}
          setSelectedRating={setSelectedRating}
          onDataRefresh={() => {
            if (id) getTrackingData(id).then((d) => { setData(d); setSelectedRating(d.client_rating || 0); });
          }}
        />
      </div>
    </div>
  );
}

// ─── Content ──────────────────────────────────────────────

export function TrackingContentV2({
  data,
  ordreId,
  hoveredStar,
  setHoveredStar,
  selectedRating,
  setSelectedRating,
  previewMode,
  onDataRefresh,
}: {
  data: TrackingData;
  ordreId?: string;
  hoveredStar: number;
  setHoveredStar: (v: number) => void;
  selectedRating: number;
  setSelectedRating: (v: number) => void;
  previewMode?: boolean;
  onDataRefresh?: () => void;
}) {
  const [carteGriseFile, setCarteGriseFile] = useState<File | null>(null);
  const carteGriseInputRef = useRef<HTMLInputElement>(null);

  const triggerCarteGriseInput = () => {
    carteGriseInputRef.current?.click();
  };

  const handleCarteGriseChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && ordreId) setCarteGriseFile(file);
    e.target.value = "";
  };
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const steps = ALL_STEPS.filter((s) => {
    if (s.key === "carte_grise_manquante" && data.has_carte_grise) return false;
    if (s.key === "devis_pret" && !data.devis_requested) return false;
    if (s.key === "en_attente_depot" && !data.devis_signed) return false;
    if (s.key === "devis_supplementaire" && !data.devis_supplementaire_requested) return false;
    if (s.key === "acompte_manquant" && !data.acompte_requested) return false;
    if (s.key === "facture_disponible" && !data.facture_available) return false;
    if (s.key === "solde_en_attente" && !data.solde_requested) return false;
    if (s.key === "paiement_valide" && !data.solde_paid) return false;
    return true;
  });

  const currentStepIndex = steps.findIndex((s) => s.key === data.tracking_step);
  const currentStep = steps[currentStepIndex] ?? steps[0];
  const isRestitue = data.tracking_step === "vehicule_restitue";

  const currentSubtitle = currentStep.key === "travaux_termines" && !data.facture_available
    ? "En attente de la facturation"
    : currentStep.key === "paiement_valide" && data.solde_paid
      ? "En attente de la restitution"
      : currentStep.subtitle;

  const showEarlyDateSteps = ["carte_grise_manquante", "en_attente_devis", "devis_pret", "devis_supplementaire", "acompte_manquant", "en_attente_depot", "vehicule_depose", "solde_en_attente"];
  const keyDate = showEarlyDateSteps.includes(data.tracking_step)
    ? { label: "Date de dépôt du véhicule", value: data.deposit_date || "À définir" }
    : { label: "Date de restitution", value: data.restitution_date || "À définir" };

  const toggle = (key: string) => setExpandedSection(expandedSection === key ? null : key);

  return (
    <div className="flex flex-col">
      {/* ── Hero ── */}
      <div className="flex flex-col px-5 pt-10 pb-4 gap-4">
        {/* Order number */}
        <span className="text-[11px] font-medium text-[#9CA3AF] tracking-wide uppercase">
          Ordre #{data.ordre_num}
        </span>

        {/* Segmented progress bar */}
        <div className="flex gap-[3px]">
          {steps.map((step, i) => {
            const done = currentStepIndex > i || isRestitue;
            const isActive = currentStepIndex === i && !isRestitue;
            return (
              <div key={step.key} className="flex-1 h-[6px] rounded-full overflow-hidden bg-[#E5E7EB]">
                {isActive ? (
                  <div className="h-full rounded-full bg-[#5C6BFF] animate-loading-bar" />
                ) : done ? (
                  <div className={`h-full rounded-full w-full ${isRestitue ? "bg-[#10B981]" : "bg-[#5C6BFF]"}`} />
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Status text */}
        <div className="flex flex-col gap-[2px]">
          <h1 className="text-[15px] font-semibold text-[#1A1A1A] m-0">
            {currentStep.title}
          </h1>
          <p className="text-[13px] font-medium text-[#6B7280] m-0">
            {currentSubtitle}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 px-5 pt-2">
        {/* ── Primary CTA ── */}
        {currentStep.cta && !isRestitue && (() => {
          const action = currentStep.cta!.action;
          const isPayAction = action === "pay_acompte" || action === "pay_solde";
          const isDevisAction = action === "view_devis";
          const isFactureAction = action === "view_facture";
          const isCarteGriseAction = action === "deposit_carte_grise";
          const hasDevisLink = data.devis_estimate_token || data.devis_sign_token;
          const hasPaymentLink = !!data.payment_link_url;

          if (isPayAction && !hasPaymentLink) return null;
          if (isDevisAction && !hasDevisLink) return null;
          if (isFactureAction && !data.facture_pdf_url) return null;


          return (
            <>
              <button
                onClick={() => {
                  if (previewMode) return;
                  if (isCarteGriseAction) { triggerCarteGriseInput(); return; }
                  if (isDevisAction) {
                    if (data.devis_estimate_token) { window.location.href = `/devis/${data.devis_estimate_token}`; return; }
                    if (data.devis_sign_token) { window.location.href = `/sign/${data.devis_sign_token}`; return; }
                  }
                  if (isFactureAction && data.facture_pdf_url) { window.open(data.facture_pdf_url, "_blank"); return; }
                  if (isPayAction && data.payment_link_url) { window.location.href = data.payment_link_url; return; }
                }}
                className="flex items-center justify-center gap-2 py-4 bg-[#5C6BFF] text-white font-semibold text-[15px] rounded-xl shadow-[0_4px_12px_rgba(92,107,255,0.3)] hover:shadow-[0_4px_16px_rgba(92,107,255,0.45)] hover:bg-[#4B5AEE] transition-all"
              >
                {currentStep.cta!.label}
              </button>
            </>
          );
        })()}

        {/* Facture CTA — show on solde step so client can review invoice before paying */}
        {data.facture_pdf_url && currentStep.key === "solde_en_attente" && (
          <button
            onClick={() => !previewMode && window.open(data.facture_pdf_url, "_blank")}
            className="flex items-center justify-center gap-2 py-4 bg-white text-[#5C6BFF] font-semibold text-[15px] rounded-xl border-2 border-[#5C6BFF] hover:bg-[#F8F9FF] transition-all"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#5C6BFF">
              <path d="M14.5 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V8.5L14.5 3zM14 4l5 5h-5V4zM6 20V5h7v5h5v10H6z" />
              <path d="M9 13h6v1H9zM9 16h4v1H9z" />
            </svg>
            Voir la facture
          </button>
        )}

        {/* Devis signature CTA */}
        {data.devis_sign_token && data.devis_sign_status === "pending" && currentStep.key !== "devis_pret" && (
          <button
            onClick={() => !previewMode && (window.location.href = `/sign/${data.devis_sign_token}`)}
            className="flex items-center justify-center gap-2 py-4 bg-white text-[#5C6BFF] font-semibold text-[15px] rounded-xl border-2 border-[#5C6BFF] hover:bg-[#F8F9FF] transition-all"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#5C6BFF">
              <path d="M14.5 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V8.5L14.5 3zM14 4l5 5h-5V4zM6 20V5h7v5h5v10H6z" />
              <path d="M9 13h6v1H9zM9 16h4v1H9z" />
            </svg>
            Signer le devis
          </button>
        )}

        {/* Carte grise uploaded — show name + replace button */}
        {data.has_carte_grise && (
          <div className="flex items-center gap-[8px] py-[10px] px-[12px] rounded-xl border border-[#E5E7EB] bg-white">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="#5C6BFF">
              <path d="M6.5 11.5L3 8l1-1 2.5 2.5L12 4l1 1-6.5 6.5z" />
            </svg>
            <span className="text-[11px] font-medium text-[#5C6BFF] flex-1">
              {data.carte_grise_name || "Carte grise"} déposée
            </span>
            <button
              onClick={() => {
                if (previewMode) return;
                triggerCarteGriseInput();
              }}
              className="text-[11px] font-medium text-[#9CA3AF] hover:text-[#5C6BFF] transition-colors"
            >
              Modifier
            </button>
          </div>
        )}

        {/* ── Add to calendar ── */}
        {data.deposit_date && showEarlyDateSteps.includes(data.tracking_step) && (() => {
          const match = data.deposit_date.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/);
          if (!match) return null;
          const [, dd, mm, yyyy, hh, mi] = match;
          const start = new Date(+yyyy, +mm - 1, +dd, +hh, +mi);
          const end = new Date(start.getTime() + 60 * 60 * 1000);
          const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d+/, "");
          const title = encodeURIComponent(`RDV ${data.org_name}${data.vehicle_plate ? ` - ${data.vehicle_plate}` : ""}`);
          const details = encodeURIComponent(`Rendez-vous garage ${data.org_name}${data.vehicle_plate ? `\nVéhicule : ${data.vehicle_plate}` : ""}`);
          const calUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${details}`;
          return (
            <a href={calUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 py-3 text-[13px] font-medium text-[#6B7280] hover:text-[#5C6BFF] transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="3" /><path d="M3 10h18M8 2v4M16 2v4" />
              </svg>
              Ajouter au calendrier
            </a>
          );
        })()}

        {/* ── Completion card ── */}
        {!isRestitue && (
          <CompletionCard
            data={data}
            ordreId={ordreId}
            previewMode={previewMode}
            onDataRefresh={onDataRefresh}
          />
        )}

        {/* ── Rating ── */}
        {isRestitue && (
          <RatingBlockV2
            hoveredStar={hoveredStar}
            setHoveredStar={setHoveredStar}
            selectedRating={selectedRating}
            setSelectedRating={setSelectedRating}
            ordreId={ordreId}
            previewMode={previewMode}
            googleReviewUrl={data.google_review_url}
          />
        )}

        {/* ── Timeline badges ── */}
        {(data.initial_devis_signed || data.additional_devis_signed ||
          (data.additional_devis_groups && data.additional_devis_groups.length > 0) ||
          (data.devis_sign_status === "signed" || (data.devis_requested && data.devis_signed)) ||
          data.facture_available ||
          (data.acompte_requested && data.acompte_paid) ||
          (data.solde_requested && data.solde_paid)) && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Étapes validées</span>
            <div className="flex flex-col mt-3 relative">
              {/* Vertical line */}
              <div className="absolute left-[7px] top-2 bottom-2 w-[2px] bg-[#E5E7EB]" />

              {data.initial_devis_signed && (
                <TimelineBadge label="Devis initial signé" status="done"
                  onClick={data.initial_devis_token && !previewMode ? () => { window.location.href = `/devis/${data.initial_devis_token}`; } : undefined}
                />
              )}

              {data.additional_devis_groups && data.additional_devis_groups.length > 0 ? (
                data.additional_devis_groups.map((g, idx) => (
                  g.signed ? (
                    <TimelineBadge key={g.key} status="done"
                      label={`Devis supplémentaire${data.additional_devis_groups!.length > 1 ? ` #${idx + 1}` : ""} signé`}
                      onClick={g.token && !previewMode ? () => { window.location.href = `/devis/${g.token}`; } : undefined}
                    />
                  ) : data.initial_devis_signed ? (
                    <TimelineBadge key={g.key} status="pending"
                      label={`Devis supplémentaire${data.additional_devis_groups!.length > 1 ? ` #${idx + 1}` : ""} en attente`}
                    />
                  ) : null
                ))
              ) : (
                <>
                  {data.devis_supplementaire_requested && data.additional_devis_signed && (
                    <TimelineBadge label="Devis supplémentaire signe" status="done"
                      onClick={data.additional_devis_token && !previewMode ? () => { window.location.href = `/devis/${data.additional_devis_token}`; } : undefined}
                    />
                  )}
                  {data.devis_supplementaire_requested && !data.additional_devis_signed && data.initial_devis_signed && (
                    <TimelineBadge label="Devis supplémentaire en attente" status="pending" />
                  )}
                </>
              )}

              {!data.initial_devis_signed && !data.additional_devis_signed &&
                !(data.additional_devis_groups && data.additional_devis_groups.length > 0) &&
                (data.devis_sign_status === "signed" || (data.devis_requested && data.devis_signed)) && (
                <TimelineBadge label="Devis signé" status="done"
                  onClick={data.devis_pdf_url && !previewMode ? () => window.open(data.devis_pdf_url, "_blank") : undefined}
                />
              )}

              {data.facture_available && (
                <TimelineBadge label="Facture émise" status="done"
                  onClick={data.facture_pdf_url && !previewMode ? () => window.open(data.facture_pdf_url, "_blank") : undefined}
                />
              )}

              {data.acompte_requested && data.acompte_paid && (
                <TimelineBadge label="Acompte réglé" status="done" />
              )}
              {data.solde_requested && data.solde_paid && (
                <TimelineBadge label="Interventions réglées" status="done" />
              )}
            </div>
          </div>
        )}

        {/* ── Key date + garage cards ── */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 flex flex-col gap-1">
            <span className="text-[11px] font-medium text-[#9CA3AF]">{keyDate.label}</span>
            <span className="text-[15px] font-bold text-[#1A1A1A]">{keyDate.value}</span>
          </div>
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 flex flex-col gap-1">
            <span className="text-[11px] font-medium text-[#9CA3AF]">Garage</span>
            <span className="text-[13px] font-semibold text-[#1A1A1A] truncate">{data.org_name || "---"}</span>
          </div>
        </div>

        {/* ── Contact bar ── */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 flex flex-col gap-3">
          {data.org_address && data.org_address !== "---" && (
            <CopyableAddressV2 value={data.org_address} />
          )}
          {(data.org_phone || data.org_email) && (
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium text-[#9CA3AF]">Coordonnées</span>
              {data.org_phone && (
                <a href={`tel:${data.org_phone}`} className="text-[13px] font-medium text-[#5C6BFF] no-underline hover:underline">{data.org_phone}</a>
              )}
              {data.org_email && (
                <a href={`mailto:${data.org_email}`} className="text-[13px] font-medium text-[#5C6BFF] no-underline hover:underline truncate">{data.org_email}</a>
              )}
            </div>
          )}
        </div>

        {/* ── WhatsApp / Phone ── */}
        {data.org_whatsapp_phone ? (
          <a
            href={`https://wa.me/${data.org_whatsapp_phone.replace(/[^0-9+]/g, "")}?text=${encodeURIComponent(`Bonjour, j'ai une question concernant mon ordre ${data.ordre_num}. Merci !`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 py-4 bg-[#25D366] text-white font-semibold text-[15px] rounded-xl shadow-[0_2px_8px_rgba(37,211,102,0.25)] hover:bg-[#22c55e] transition-all no-underline"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              <path fillRule="evenodd" d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 0 0 .613.613l4.458-1.495A11.952 11.952 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z" />
            </svg>
            Poser une question
          </a>
        ) : data.org_phone ? (
          <a
            href={`tel:${data.org_phone}`}
            className="flex items-center justify-center gap-2 py-4 bg-white text-[#1A1A1A] font-semibold text-[15px] rounded-xl border border-[#E5E7EB] hover:bg-[#F8F9FF] transition-all no-underline"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#5C6BFF">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
            </svg>
            Poser une question
          </a>
        ) : null}

        {/* ── Collapsible: Détails de la demande ── */}
        <CollapsibleSection
          title="Détails de l'ordre"
          expanded={expandedSection === "details"}
          onToggle={() => toggle("details")}
        >
          <div className="flex flex-col">
            <SummaryRow label="Ordre" value={`#${data.ordre_num}`} />
            <SummaryRow label="Véhicule" value={data.vehicle_plate || "---"} />
            <SummaryRow label="Service" value={data.service_type || "---"} />
            <SummaryRow label="Type" value={data.intervention_type || "---"} />
            <SummaryRow label="Interventions" value={data.interventions?.join(", ") || "---"} />
            <SummaryRow label="Date de dépôt" value={data.deposit_date || "---"} />
            <SummaryRow label="Date de restitution" value={data.restitution_date || "---"} last />
          </div>
        </CollapsibleSection>

        {/* ── Collapsible: Options de mobilite ── */}
        {data.mobility_option && (
          <CollapsibleSection
            title="Option de mobilité"
            expanded={expandedSection === "mobility"}
            onToggle={() => toggle("mobility")}
          >
            <div className="flex flex-col gap-2">
              {MOBILITY_OPTIONS.map((opt) => {
                const isSelected = data.mobility_option === opt.key;
                return (
                  <div key={opt.key} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${isSelected ? "bg-[#5C6BFF]" : "border border-[#E5E7EB]"}`}>
                      {isSelected && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-[13px] ${isSelected ? "font-medium text-[#1A1A1A]" : "text-[#9CA3AF]"}`}>
                      {opt.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* ── Collapsible: Options pneumatiques ── */}
        {data.tire_option && (
          <CollapsibleSection
            title="Option pneumatique"
            expanded={expandedSection === "tires"}
            onToggle={() => toggle("tires")}
          >
            <div className="flex flex-col gap-2">
              {TIRE_OPTIONS.map((opt) => {
                const isSelected = data.tire_option === opt.key;
                return (
                  <div key={opt.key} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${isSelected ? "bg-[#5C6BFF]" : "border border-[#E5E7EB]"}`}>
                      {isSelected && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-[13px] ${isSelected ? "font-medium text-[#1A1A1A]" : "text-[#9CA3AF]"}`}>
                      {opt.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}
      </div>

      <style>{`
@keyframes confetti-burst {
          0% { opacity: 1; transform: translate(0, 0) rotate(0deg) scale(1); }
          100% { opacity: 0; transform: translate(var(--tx), var(--ty)) rotate(var(--rot)) scale(0.5); }
        }
        @keyframes glow-fade {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes slide-up-fade {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Fullscreen carte grise upload overlay */}
      {/* Hidden file input for carte grise */}
      <input
        ref={carteGriseInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleCarteGriseChange}
      />

      {carteGriseFile && ordreId && (
        <CarteGriseUploadOverlay
          file={carteGriseFile}
          ordreId={ordreId}
          onClose={() => setCarteGriseFile(null)}
          onSuccess={() => {
            setCarteGriseFile(null);
            onDataRefresh?.();
          }}
        />
      )}
    </div>
  );
}

// ─── Completion card ──────────────────────────────────────

const MOBILITY_CARDS = [
  { key: "attente", label: "Attendre sur place", subtitle: "Environ 1h30", icon: "clock", tag: null, premium: false },
  { key: "velo", label: "Vélo électrique", subtitle: "Gratuit", icon: "bike", tag: null, premium: false },
  { key: "pret", label: "Véhicule de prêt", subtitle: "Zone limitée", icon: "car", tag: null, premium: false },
  { key: "location", label: "Véhicule de location", subtitle: "Liberté totale", icon: "key", tag: "Premium", premium: true },
];

const TIRE_CARDS = [
  { key: "premier_prix", label: "Premier prix" },
  { key: "intermediaires", label: "Intermédiaires" },
  { key: "premium", label: "Premium" },
];

const INTERVENTION_TYPE_LABELS: Record<string, string> = {
  entretien: "Entretien / Révision",
  reparation: "Réparation / Panne",
  diagnostic: "Diagnostic",
  controle_technique: "Contrôle technique",
  pneumatique: "Pneus",
  carrosserie: "Carrosserie / Vitrage",
};

function CompletionCard({
  data,
  ordreId,
  previewMode,
  onDataRefresh,
}: {
  data: TrackingData;
  ordreId?: string;
  previewMode?: boolean;
  onDataRefresh?: () => void;
}) {
  const [activeField, setActiveField] = useState<string | null>(null);
  const [email, setEmail] = useState(data.client_email || "");
  const [km, setKm] = useState(data.client_km ? String(data.client_km) : data.last_km ? String(data.last_km) : "");
  const [kmError, setKmError] = useState("");
  const [comments, setComments] = useState(data.client_comments || "");
  const [address, setAddress] = useState(data.client_address || "");
  const [interventions, setInterventions] = useState<PublicIntervention[]>([]);
  const [selectedInterventions, setSelectedInterventions] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [justSaved, setJustSaved] = useState<string | null>(null);
  const [showAnswered, setShowAnswered] = useState(false);

  const needsMobility = !data.mobility_option;
  const needsTires = (data.service_type || "").toLowerCase().includes("pneum") && !data.tire_option;
  const needsEmail = !data.client_email;
  const needsKm = !data.client_km;
  const needsAddress = !data.client_address;
  const needsInterventionDetail = !data.client_intervention_detail;
  const needsComments = !data.client_comments;

  const optionalKeys = new Set(["comments"]);
  const fields = [
    needsMobility && { key: "mobility", label: "Mobilité" },
    needsTires && { key: "tires", label: "Pneus" },
    needsInterventionDetail && { key: "intervention_detail", label: "Interventions" },
    needsEmail && { key: "email", label: "Email" },
    needsKm && { key: "km", label: "Km" },
    needsAddress && { key: "address", label: "Adresse" },
    needsComments && { key: "comments", label: "Commentaire" },
  ].filter(Boolean) as { key: string; label: string }[];

  const totalFields = 5 + (needsTires ? 1 : 0); // mobility + email + km + address + intervention_detail + tires if applicable
  const completedCount = totalFields - fields.filter((f) => !optionalKeys.has(f.key)).length;

  // Fetch org interventions when the chip is tapped
  useEffect(() => {
    if (activeField === "intervention_detail" && data.org_id && interventions.length === 0) {
      getPublicInterventions(data.org_id).then((list) => setInterventions(list.filter((i) => i.active))).catch(() => {});
    }
  }, [activeField, data.org_id]);

  if (fields.length === 0) return null;

  const submitField = async (payload: TrackingCompletionPayload) => {
    if (previewMode || !ordreId) return;
    setSubmitting(true);
    try {
      await submitTrackingCompletion(ordreId, payload);
      const savedKey = activeField;
      setActiveField(null);
      setJustSaved(savedKey);
      setTimeout(() => setJustSaved(null), 2000);
      onDataRefresh?.();
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-semibold text-[#1A1A1A]">
            Complétez vos informations
          </span>
          <span className="text-[11px] font-medium text-[#9CA3AF]">
            {completedCount}/{totalFields}
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-[4px] bg-[#F3F4F6] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#5C6BFF] rounded-full transition-all duration-500"
            style={{ width: `${(completedCount / totalFields) * 100}%` }}
          />
        </div>
      </div>

      {/* Chips — missing fields */}
      <div className="flex flex-wrap gap-1.5 px-4 pb-3">
        {fields.map((f) => {
          const isActive = activeField === f.key;
          const isSaved = justSaved === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setActiveField(isActive ? null : f.key)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-all"
              style={{
                background: isSaved ? "#F0FDF4" : isActive ? "#EEF0FF" : "#F9FAFB",
                color: isSaved ? "#16A34A" : isActive ? "#5C6BFF" : "#6B7280",
                border: isSaved ? "1px solid #BBF7D0" : isActive ? "1px solid #C7D2FE" : "1px solid #E5E7EB",
              }}
            >
              {isSaved ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#16A34A"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" /></svg>
              ) : (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={isActive ? "#5C6BFF" : "#9CA3AF"} strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              )}
              {isSaved ? "Enregistré" : f.label}
            </button>
          );
        })}
      </div>

      {/* Toggle to show/edit previous answers */}
      {completedCount > 0 && !activeField && (
        showAnswered ? (
          <div className="px-4 pb-3 flex flex-col gap-1">
            {!needsMobility && (
              <AnsweredRow label="Mobilité" value={MOBILITY_CARDS.find((c) => c.key === data.mobility_option)?.label || data.mobility_option || ""} onEdit={() => setActiveField("mobility")} />
            )}
            {!needsTires && data.tire_option && (
              <AnsweredRow label="Pneus" value={TIRE_CARDS.find((c) => c.key === data.tire_option)?.label || data.tire_option || ""} onEdit={() => setActiveField("tires")} />
            )}
            {!needsInterventionDetail && (
              <AnsweredRow label="Interventions" value={data.client_intervention_detail || ""} onEdit={() => setActiveField("intervention_detail")} />
            )}
            {!needsEmail && (
              <AnsweredRow label="Email" value={data.client_email || ""} onEdit={() => setActiveField("email")} />
            )}
            {!needsKm && (
              <AnsweredRow label="Km" value={data.client_km ? `${data.client_km.toLocaleString("fr-FR")} km` : ""} onEdit={() => setActiveField("km")} />
            )}
            {!needsAddress && (
              <AnsweredRow label="Adresse" value={data.client_address || ""} onEdit={() => setActiveField("address")} />
            )}
            <button onClick={() => setShowAnswered(false)} className="text-[11px] text-[#9CA3AF] hover:text-[#6B7280] transition-colors self-start mt-1">
              Masquer
            </button>
          </div>
        ) : (
          <div className="px-4 pb-3">
            <button onClick={() => setShowAnswered(true)} className="text-[11px] text-[#9CA3AF] hover:text-[#6B7280] transition-colors">
              Modifier mes réponses
            </button>
          </div>
        )
      )}

      {/* Expanded content */}
      {activeField === "mobility" && (
        <div className="px-4 pb-4 flex flex-col gap-2">
          <span className="text-[12px] font-medium text-[#6B7280]">
            Comment souhaitez-vous vous déplacer pendant l'intervention ?
          </span>
          <div className="flex flex-col gap-1.5">
            {MOBILITY_CARDS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => submitField({ mobility_option: opt.key })}
                disabled={submitting}
                className="flex items-center gap-3 p-3 rounded-xl transition-all disabled:opacity-50"
                style={{
                  border: opt.premium ? "1.5px solid #1A1A1A" : "1.5px solid #E5E7EB",
                  background: opt.premium ? "#FAFAFA" : "white",
                }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: opt.premium ? "#1A1A1A" : "#F3F4F6" }}
                >
                  <MobilityIcon type={opt.icon} color={opt.premium ? "#FFFFFF" : "#374151"} />
                </div>
                <div className="flex flex-col items-start flex-1 min-w-0">
                  <span className={`text-[13px] font-semibold ${opt.premium ? "text-[#1A1A1A]" : "text-[#374151]"}`}>{opt.label}</span>
                  <span className="text-[11px] text-[#9CA3AF]">{opt.subtitle}</span>
                </div>
                {opt.tag && (
                  <span className="text-[10px] font-bold tracking-wide uppercase px-2 py-0.5 rounded bg-[#1A1A1A] text-white flex-shrink-0">
                    {opt.tag}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeField === "tires" && (
        <div className="px-4 pb-4 flex flex-col gap-2">
          <span className="text-[12px] font-medium text-[#6B7280]">
            Quel type de pneus préférez-vous ?
          </span>
          <div className="flex flex-col gap-1.5">
            {TIRE_CARDS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => submitField({ tire_option: opt.key })}
                disabled={submitting}
                className="flex items-center justify-between py-3 px-4 rounded-xl transition-all hover:border-[#5C6BFF] hover:bg-[#F8F9FF] disabled:opacity-50"
                style={{ border: "1.5px solid #E5E7EB" }}
              >
                <span className="text-[13px] font-medium text-[#374151]">{opt.label}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#9CA3AF"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" /></svg>
              </button>
            ))}
          </div>
        </div>
      )}

      {activeField === "email" && (
        <div className="px-4 pb-4 flex flex-col gap-2">
          <span className="text-[12px] font-medium text-[#6B7280]">
            Pour recevoir vos devis et factures
          </span>
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (email.includes("@")) submitField({ email }); }} autoComplete="on">
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={(e) => { setTimeout(() => e.target.scrollIntoView({ behavior: "smooth", block: "center" }), 350); }}
              placeholder="votre@email.fr"
              className="flex-1 min-w-0 text-[13px] px-3 py-2.5 rounded-xl outline-none"
              style={{ border: "1.5px solid #E5E7EB", fontFamily: "inherit" }}
            />
            <button
              type="submit"
              disabled={submitting || !email.includes("@")}
              className="px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all disabled:opacity-40"
              style={{ background: "#5C6BFF" }}
            >
              OK
            </button>
          </form>
        </div>
      )}

      {activeField === "km" && (
        <div className="px-4 pb-4 flex flex-col gap-2">
          <span className="text-[12px] font-medium text-[#6B7280]">
            Kilométrage actuel de votre véhicule
          </span>
          <div className="flex gap-2">
            <input
              type="number"
              value={km}
              onChange={(e) => {
                setKm(e.target.value);
                const val = parseInt(e.target.value);
                if (data.last_km && val && val < data.last_km) {
                  setKmError(`Le kilométrage ne peut pas être inférieur au dernier relevé (${data.last_km.toLocaleString("fr-FR")} km)`);
                } else {
                  setKmError("");
                }
              }}
              onFocus={(e) => { setTimeout(() => e.target.scrollIntoView({ behavior: "smooth", block: "center" }), 350); }}
              placeholder={data.last_km ? `Dernier relevé : ${data.last_km.toLocaleString("fr-FR")} km` : "ex: 45000"}
              className="flex-1 min-w-0 text-[13px] px-3 py-2.5 rounded-xl outline-none"
              style={{ border: `1.5px solid ${kmError ? "#EF4444" : "#E5E7EB"}`, fontFamily: "inherit" }}
            />
            <button
              onClick={() => km && !kmError && submitField({ km: parseInt(km) })}
              disabled={submitting || !km || !!kmError}
              className="px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all disabled:opacity-40"
              style={{ background: "#5C6BFF" }}
            >
              OK
            </button>
          </div>
          {kmError && (
            <span className="text-[11px] font-medium text-[#EF4444]">{kmError}</span>
          )}
        </div>
      )}

      {activeField === "intervention_detail" && (
        <div className="px-4 pb-4 flex flex-col gap-2">
          <span className="text-[12px] font-medium text-[#6B7280]">
            Sélectionnez les interventions souhaitées
          </span>
          {interventions.length === 0 ? (
            <div className="py-4 text-center text-[12px] text-[#9CA3AF]">Chargement...</div>
          ) : (
            <div className="overflow-y-auto rounded-xl border border-[#E5E7EB] bg-white" style={{ maxHeight: 240 }}>
              {Object.entries(INTERVENTION_TYPE_LABELS).map(([cat, catLabel]) => {
                const items = interventions.filter((i) => i.type === cat).sort((a, b) => a.name.localeCompare(b.name, "fr"));
                if (!items.length) return null;
                return (
                  <div key={cat}>
                    <div className="px-3 py-1.5 text-[11px] font-semibold text-[#9CA3AF] bg-[#F9FAFB] sticky top-0 uppercase tracking-wide">{catLabel}</div>
                    {items.map((item) => {
                      const isSelected = selectedInterventions.includes(item.name);
                      return (
                        <button
                          key={item.id}
                          onClick={() => setSelectedInterventions((prev) =>
                            isSelected ? prev.filter((n) => n !== item.name) : [...prev, item.name]
                          )}
                          className="flex items-center gap-2 w-full px-3 py-2.5 text-left transition-colors hover:bg-[#F8F9FF]"
                        >
                          <div
                            className="w-4 h-4 rounded flex-shrink-0 flex items-center justify-center transition-colors"
                            style={{ border: isSelected ? "none" : "1.5px solid #D1D5DB", background: isSelected ? "#5C6BFF" : "white" }}
                          >
                            {isSelected && (
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" /></svg>
                            )}
                          </div>
                          <span className={`text-[13px] ${isSelected ? "text-[#5C6BFF] font-medium" : "text-[#374151]"}`}>{item.name}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
          {selectedInterventions.length > 0 && (
            <button
              onClick={() => submitField({ intervention_detail: selectedInterventions.join(", ") })}
              disabled={submitting}
              className="self-end px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all disabled:opacity-40"
              style={{ background: "#5C6BFF" }}
            >
              Valider ({selectedInterventions.length})
            </button>
          )}
        </div>
      )}

      {activeField === "address" && (
        <div className="px-4 pb-4 flex flex-col gap-2">
          <span className="text-[12px] font-medium text-[#6B7280]">
            Votre adresse personnelle (pour le transport du véhicule si besoin)
          </span>
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (address.trim()) submitField({ address: address.trim() }); }} autoComplete="on">
            <input
              type="text"
              name="street-address"
              autoComplete="street-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onFocus={(e) => { setTimeout(() => e.target.scrollIntoView({ behavior: "smooth", block: "center" }), 350); }}
              placeholder="ex: 12 rue de la Paix, 75002 Paris"
              className="flex-1 min-w-0 text-[13px] px-3 py-2.5 rounded-xl outline-none"
              style={{ border: "1.5px solid #E5E7EB", fontFamily: "inherit" }}
            />
            <button
              type="submit"
              disabled={submitting || !address.trim()}
              className="px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all disabled:opacity-40"
              style={{ background: "#5C6BFF" }}
            >
              OK
            </button>
          </form>
        </div>
      )}

      {activeField === "comments" && (
        <div className="px-4 pb-4 flex flex-col gap-2">
          <span className="text-[12px] font-medium text-[#6B7280]">
            Informations supplémentaires pour le garage
          </span>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            onFocus={(e) => { setTimeout(() => e.target.scrollIntoView({ behavior: "smooth", block: "center" }), 350); }}
            placeholder="ex: bruit suspect au freinage, voyant moteur allumé..."
            rows={3}
            className="text-[13px] px-3 py-2.5 rounded-xl outline-none resize-none"
            style={{ border: "1.5px solid #E5E7EB", fontFamily: "inherit" }}
          />
          <button
            onClick={() => comments.trim() && submitField({ comments: comments.trim() })}
            disabled={submitting || !comments.trim()}
            className="self-end px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: "#5C6BFF" }}
          >
            Envoyer
          </button>
        </div>
      )}
    </div>
  );
}

function MobilityIcon({ type, color = "#5C6BFF" }: { type: string; color?: string }) {
  const p = { width: 22, height: 22, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 1.8, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  if (type === "clock") return <svg {...p}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
  if (type === "bike") return <svg {...p}><circle cx="5.5" cy="17.5" r="3.5" /><circle cx="18.5" cy="17.5" r="3.5" /><path d="M15 6a1 1 0 100-2 1 1 0 000 2zM12 17.5V14l-3-3 4-3 2 3h2" /></svg>;
  if (type === "car") return <svg {...p}><path d="M5 17h2m10 0h2M5 11l2-5h10l2 5M3 11h18v6H3z" /><circle cx="7" cy="17" r="1" fill={color} /><circle cx="17" cy="17" r="1" fill={color} /></svg>;
  if (type === "key") return <svg {...p}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>;
  return null;
}

function AnsweredRow({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="text-[11px] text-[#9CA3AF] flex-shrink-0">{label}</span>
        <span className="text-[11px] font-medium text-[#374151] truncate">{value}</span>
      </div>
      <button onClick={onEdit} className="text-[11px] text-[#9CA3AF] hover:text-[#5C6BFF] transition-colors flex-shrink-0 ml-2">
        Modifier
      </button>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────

function TimelineBadge({ label, status, onClick }: { label: string; status: "done" | "pending"; onClick?: () => void }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`flex items-center gap-3 py-2 pl-0 relative ${onClick ? "cursor-pointer hover:opacity-80" : ""}`}
    >
      {/* Dot */}
      <div className={`w-4 h-4 rounded-full shrink-0 flex items-center justify-center z-10 ${status === "done" ? "bg-[#5C6BFF]" : "bg-white border-2 border-[#F59E0B]"}`}>
        {status === "done" && (
          <svg width="8" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <span className={`text-[13px] font-medium ${status === "done" ? "text-[#1A1A1A]" : "text-[#F59E0B]"}`}>
        {label}
      </span>
      {onClick && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="#9CA3AF" className="ml-auto shrink-0">
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
        </svg>
      )}
    </Tag>
  );
}

function CollapsibleSection({ title, expanded, onToggle, children }: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-[#FAFBFF] transition-colors"
      >
        <span className="text-[13px] font-semibold text-[#1A1A1A]">{title}</span>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="#9CA3AF"
          className="transition-transform duration-200"
          style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
        </svg>
      </button>
      <div
        className="overflow-hidden transition-all duration-200"
        style={{ maxHeight: expanded ? 600 : 0, opacity: expanded ? 1 : 0 }}
      >
        <div className="px-4 pb-4">{children}</div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <>
      <div className="flex justify-between items-start py-2">
        <span className="text-[11px] font-medium text-[#9CA3AF]">{label}</span>
        <span className="text-[13px] font-medium text-[#1A1A1A] text-right ml-4">{value}</span>
      </div>
      {!last && <div className="border-b border-[#F3F4F6]" />}
    </>
  );
}

function CopyableAddressV2({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = useRef(false);

  const openInMaps = useCallback(() => {
    const q = encodeURIComponent(value);
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank");
  }, [value]);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [value]);

  const onPointerDown = () => {
    didLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      openInMaps();
    }, 600);
  };

  const onPointerUp = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-[#9CA3AF]">Adresse</span>
      <div
        className="flex items-start justify-between gap-2 cursor-pointer select-none group"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={() => { if (!didLongPress.current) copyToClipboard(); }}
      >
        <span className="text-[13px] font-medium text-[#5C6BFF] whitespace-pre-line group-hover:underline">
          {value}
        </span>
        <div className="shrink-0 mt-[2px]">
          {copied ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#10B981">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#9CA3AF">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
            </svg>
          )}
        </div>
      </div>
      <span className="text-[11px] text-[#9CA3AF]">
        {copied ? "Copié !" : "Appui long pour ouvrir dans Maps"}
      </span>
    </div>
  );
}

// ─── Rating ───────────────────────────────────────────────

const RATING_LABELS = ["", "Mauvais", "Décevant", "Correct", "Très bien", "Excellent"];
const CONFETTI_COLORS = ["#5C6BFF", "#43E4CB", "#F59E0B", "#EC4899", "#8B5CF6", "#10B981"];

function RatingBlockV2({
  hoveredStar, setHoveredStar, selectedRating, setSelectedRating, ordreId, previewMode, googleReviewUrl,
}: {
  hoveredStar: number;
  setHoveredStar: (v: number) => void;
  selectedRating: number;
  setSelectedRating: (v: number) => void;
  ordreId?: string;
  previewMode?: boolean;
  googleReviewUrl?: string;
}) {
  const [animPhase, setAnimPhase] = useState<"idle" | "bounce" | "done">("idle");
  const [bounceIndex, setBounceIndex] = useState(-1);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showGoogleReview, setShowGoogleReview] = useState(false);
  const [reviewText, setReviewText] = useState("Excellent service ! Réparation rapide et soignée, je recommande vivement.");
  const [confettiParticles] = useState(() =>
    Array.from({ length: 24 }, (_, i) => ({
      id: i,
      x: (Math.random() - 0.5) * 200,
      y: -(Math.random() * 120 + 40),
      rotate: Math.random() * 360,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      delay: Math.random() * 200,
      size: Math.random() * 4 + 3,
    }))
  );

  const handleRate = (star: number) => {
    if (previewMode || animPhase === "bounce") return;
    setSelectedRating(star);
    setAnimPhase("bounce");
    setBounceIndex(0);

    for (let i = 0; i <= star; i++) {
      setTimeout(() => setBounceIndex(i), i * 80);
    }
    setTimeout(() => {
      setAnimPhase("done");
      setBounceIndex(-1);
      if (star >= 4) {
        setShowConfetti(true);
        setShowThankYou(true);
        setTimeout(() => setShowConfetti(false), 1200);
        setTimeout(() => setShowThankYou(false), 3000);
      }
      if (star === 5) {
        setTimeout(() => setShowGoogleReview(true), 1500);
      }
    }, star * 80 + 300);

    if (ordreId) submitTrackingRating(ordreId, star).catch(() => {});
  };

  const displayRating = hoveredStar || selectedRating;

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] flex flex-col items-center gap-3 py-6 relative overflow-visible">
      <span className="text-[15px] font-semibold text-[#1A1A1A]">
        Comment s'est passée votre expérience ?
      </span>

      <div className="flex gap-4 py-2 relative">
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
            {confettiParticles.map((p) => (
              <div
                key={p.id}
                className="absolute rounded-full"
                style={{
                  width: p.size,
                  height: p.size,
                  backgroundColor: p.color,
                  left: "50%",
                  top: "50%",
                  animation: `confetti-burst 1s ease-out ${p.delay}ms forwards`,
                  ["--tx" as string]: `${p.x}px`,
                  ["--ty" as string]: `${p.y}px`,
                  ["--rot" as string]: `${p.rotate}deg`,
                  opacity: 0,
                }}
              />
            ))}
          </div>
        )}

        {[1, 2, 3, 4, 5].map((star) => {
          const active = star <= displayRating;
          const isBouncing = animPhase === "bounce" && star - 1 === bounceIndex && star <= selectedRating;
          return (
            <button
              key={star}
              onMouseEnter={() => animPhase !== "bounce" && setHoveredStar(star)}
              onMouseLeave={() => setHoveredStar(0)}
              onClick={() => handleRate(star)}
              className="relative"
              style={{
                transform: isBouncing ? "translateY(-12px) scale(1.3)" : active ? "scale(1)" : "scale(0.9)",
                transition: "transform 250ms cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
            >
              {active && animPhase === "done" && selectedRating >= 4 && (
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: "radial-gradient(circle, rgba(92,107,255,0.25) 0%, transparent 70%)",
                    transform: "scale(1.8)",
                    animation: "glow-fade 1.5s ease-out forwards",
                  }}
                />
              )}
              <svg
                width="40" height="40" viewBox="0 0 24 24"
                fill={active ? "#5C6BFF" : "none"}
                stroke={active ? "#5C6BFF" : "#D1D5DB"}
                strokeWidth="1.5"
                style={{
                  transition: "fill 120ms, stroke 120ms",
                  transitionDelay: `${(star - 1) * 40}ms`,
                  filter: active && selectedRating >= 4 ? "drop-shadow(0 0 6px rgba(92,107,255,0.4))" : "none",
                }}
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </button>
          );
        })}
      </div>

      <div className="h-5 flex items-center">
        {showThankYou ? (
          <span className="text-[14px] font-semibold text-[#5C6BFF]" style={{ animation: "slide-up-fade 400ms ease-out" }}>
            Merci beaucoup !
          </span>
        ) : displayRating > 0 ? (
          <span className="text-[13px] font-medium text-[#5C6BFF]">
            {RATING_LABELS[displayRating]}
          </span>
        ) : null}
      </div>

      {showGoogleReview && googleReviewUrl && (
        <div
          className="flex flex-col gap-3 mx-4 mb-2 p-4 bg-[#F8F9FA] rounded-xl border border-[#E5E7EB]"
          style={{ animation: "slide-up-fade 400ms ease-out" }}
        >
          <div className="flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#FBBC04" stroke="#FBBC04" strokeWidth="0.5" />
            </svg>
            <span className="text-[14px] font-semibold text-[#1A1A1A]">
              Partagez votre expérience sur Google
            </span>
          </div>
          <textarea
            className="w-full rounded-lg border border-[#E5E7EB] p-3 text-[13px] text-[#374151] resize-none focus:outline-none focus:border-[#5C6BFF] transition-colors"
            rows={3}
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
          />
          <button
            onClick={() => {
              navigator.clipboard.writeText(reviewText).catch(() => {});
              window.open(googleReviewUrl, "_blank");
            }}
            className="flex items-center justify-center gap-2 py-3 bg-[#1A73E8] text-white font-semibold text-[14px] rounded-xl hover:bg-[#1565C0] transition-all"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            Laisser un avis Google
          </button>
          <span className="text-[11px] text-[#9CA3AF] text-center">
            Le texte a été copié dans votre presse-papiers
          </span>
        </div>
      )}
    </div>
  );
}
